import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "./api";

export type User = { id: string; email: string; full_name: string; phone: string | null; locale: string };
export type Membership = { org_id: string; org_name: string; org_kind: string; plan_code: string; role: Role };
export type Role = "proprietaire" | "gestionnaire" | "collaborateur" | "comptable" | "lecture";

type Session = {
  user: User | null;
  memberships: Membership[];
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["session"], queryFn: () => get<{ user: User | null; memberships: Membership[] }>("/api/v1/auth/session"), staleTime: 60_000 });
  const value: Session = {
    user: q.data?.user ?? null,
    memberships: q.data?.memberships ?? [],
    loading: q.isLoading,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ["session"] });
    },
    logout: async () => {
      await post("/api/v1/auth/logout");
      qc.clear();
      await qc.invalidateQueries({ queryKey: ["session"] });
    },
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("SessionProvider manquant");
  return s;
}

const ROLE_PERMS: Record<Role, string[]> = {
  proprietaire: ["read", "operate", "decide", "finance", "members", "billing"],
  gestionnaire: ["read", "operate", "decide", "finance", "members"],
  collaborateur: ["read", "operate"],
  comptable: ["read", "finance"],
  lecture: ["read"],
};

export type Perm = "read" | "operate" | "decide" | "finance" | "members" | "billing";

/** Indication d'interface seulement : le serveur vérifie toujours les droits. */
export function can(role: Role | undefined, perm: Perm): boolean {
  return !!role && ROLE_PERMS[role].includes(perm);
}

export const ROLE_LABELS: Record<Role, string> = {
  proprietaire: "Propriétaire",
  gestionnaire: "Gestionnaire",
  collaborateur: "Collaborateur",
  comptable: "Comptable",
  lecture: "Lecture seule",
};
